/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-loop-func */
import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Brain, ChevronRight, HelpCircle, NotebookText } from 'lucide-react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { bindActionCreators, Dispatch } from 'redux'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import Chat from 'components/Chat'
import { AppSendMessageIcon } from 'components/Common/AppSvgIcons/AppSendMessageIcon'
import { AppPlayIcon } from 'components/Common/AppSvgIcons/AppPlayIcon'
import Avatar from 'appAssets/appImages/Avatar.png'
import { ReactComponent as QuickChatIcon } from 'assets/images/quick-chat-icon.svg'
import { ReactComponent as ArrowsPointingInIcon } from 'assets/images/arrows-pointing-in.svg'
import { ReactComponent as FolderIcon } from 'assets/images/folder.svg'
import { RootState } from 'App'
import { saveUserFileAction } from 'store/actions/common'
import { getUsersAssessmentResponseByQuestionAction } from 'store/actions/owner/initialAssessment'
import { getChatUsersAction, chatUnreadMessageCountAction } from 'store/actions/user'
import { getAuthToken } from 'lib/auth'
import {
  useGetChatSessionsQuery,
  useSummarySessionTitleMutation,
  useCreateFlowChatSessionMutation,
  useGetBusinessProfileQuery,
  OnboardingFlowType,
  OnboardingFlowStepTypes,
  OnboardingFlowStepStatus,
  Tool,
  useGetAllToolsQuery,
} from '__generated__/api-types-and-hooks'
import { useInfiniteGetMessagesFromBotQuery } from '__generated__/infinite-api-types-and-hooks'
import { LoadingIndicator } from 'stream-chat-react'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppPaperClipIcon } from 'components/Common/AppSvgIcons/AppPaperClipIcon'
import CircleNotchIcon from 'components/Common/SvgIcons/CircleNotchIcon'
import 'components/AppOnboarding/AppChatbot/AppChatBotFlows/styles.css'
import { getInitials } from 'components/Chat/helper'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { AppFileUploadField } from 'components/Common/AppFileUploadField/AppFileUploadField.component'
import {
  AppDynamicChatMessage,
  UIComponentProps,
} from 'components/AppOnboarding/AppChatbot/AppDynamicChatMessage/AppDynamicChatMessage.component'
import { useForm } from 'react-hook-form'
import { getTenantId } from 'utils/getTenantId'
import { OPEN_COACH_LOGIC_HELP_EVENT, helpDesk } from 'utils/helper'
import { AppAudioRecorder } from 'components/Common/AppAudioRecorder/AppAudioRecorder.component'
import { AppVoicePlayback } from 'components/Common/AppVoicePlayback/AppVoicePlayback.component'
import { AppVoiceIndicator } from 'components/Common/AppVoiceIndicator/AppVoiceIndicator.component'
import { extractTextForTTS, isAudioRecordingSupported } from 'utils/audioUtils'
import { AppTask } from 'components/AppKanbanBoard/AppTask/AppTask.component'
import { AppTaskCreationConfirmation } from 'components/AppKanbanBoard/AppTaskCreationConfirmation/AppTaskCreationConfirmation.component'
import { UserTask, UserTaskSource } from '__generated__/api-types-and-hooks'
import { useGetBusinessProfile } from 'appHooks/useGetBusinessProfile'
import AppCrossIcon from 'components/Common/AppSvgIcons/AppModalCrossIcon'
import { AppToolTip } from 'components/Common/AppToolTip/AppToolTip.component'
import { SafeMarkdown } from 'components/Common/SafeMarkdown/SafeMarkdown.component'
import { AppCoachReportBlocks } from 'components/Common/AppCoachReportBlock/AppCoachReportBlock.component'
import {
  CoachChatContext,
  isCoachSessionId,
} from 'components/Common/AppCoachReportBlock/coachChatContext'
import { useFlashParams } from 'hooks/useFlashParams'
import { ReactComponent as PenIcon } from 'assets/svgs/pen-icon.svg'
import { useVoicePlayback } from 'hooks/useVoicePlayback'
import { useFeatureFlag } from 'hooks/userFeatureFlag'
import { useCoachTarget } from 'components/Common/AppFloatingChat/useCoachTarget.hook'
import { showErrorToast } from 'utils/toastHelpers'
import { AppCoachMemory } from 'components/Common/AppCoachMemory/AppCoachMemory.component'
import { USER_ROLES } from 'config'
import { BotMessageLink, UserMessageLink } from 'components/Common/AppFloatingChat/chatLinks'
import { streamContextFields } from 'components/Common/AppFloatingChat/streamRequest'
import { toThreadPreview } from 'components/Common/AppFloatingChat/threadPreview'
import {
  applyStreamComplete,
  MessageAttachment,
  parseStoredJsonArray,
  StreamCitation,
} from 'components/Common/AppFloatingChat/streamComplete'

// Note: Chat context generation functions removed - play descriptions are now empty by default

let uploadsStarted = 0
/** An id for an upload this chat starts, so it knows its own result. */
const nextUploadId = () => {
  uploadsStarted += 1
  return `coach-chat-upload-${uploadsStarted}`
}

/**
 * The files an upload of `count` files gave back, in the order they went up: one file comes back
 * as itself and several as a list. Undefined when `result` is not such an upload (a failed one, or
 * one of another number of files).
 */
const uploadResultsOf = (
  result: unknown,
  count: number
): { name: string; url: string }[] | undefined => {
  const files: any[] = count > 1 && Array.isArray(result) ? result : [result]
  const complete =
    files.length === count && files.every((file) => file && !Array.isArray(file) && file.url)
  return complete ? files : undefined
}

/**
 * The attached files that finished uploading: their links stay in the text (legacy coach), and
 * Ask CL v2 reads the files themselves.
 */
const uploadedFilesOf = (
  files: { file: File; status: string; uploadedUrl?: string; uploadedName?: string }[]
) => {
  const uploaded = files.filter(
    (pf) => pf.status === 'uploaded' && pf.uploadedUrl && pf.uploadedName
  )
  return {
    uploaded,
    fileLinks: uploaded.map((pf) => `[${pf.uploadedName}](${pf.uploadedUrl})`).join('\n'),
    attachments: uploaded.map((pf) => ({
      url: pf.uploadedUrl,
      name: pf.uploadedName,
      mime: pf.file.type || '',
    })),
  }
}

interface Message {
  id: string
  sender: 'user' | 'bot'
  content: string | UIComponentProps
  timestamp: Date
  createdAt?: string
  isEdited?: boolean | null
  isVoiceMessage?: boolean
  voiceDuration?: number
  isEditingMessage?: boolean
  editingOriginalMessageIndex?: number
  editingStepType?: OnboardingFlowStepTypes | null
  editingStepContent?: UIComponentProps | null
  editingMessageText?: string
  reportBlocks?: any[]
  // Web pages the answer relied on (its Sources section is already in the text)
  citations?: StreamCitation[]
  // Files sent with a user message (from the stored history)
  attachments?: MessageAttachment[]
  // Streamed gpt-5 reasoning summary, accumulated from reasoning_token deltas.
  // Shown in a collapsible "Thoughts" panel above the answer.
  reasoningText?: string
}

// Which business the conversation is about, sent on the 'session' line.
interface CoachStreamScope {
  businessId: string
  businessName?: string | null
  audience: 'business' | 'advisor'
}

interface StreamingResponse {
  type: 'session' | 'content' | 'tool_call' | 'complete' | 'error' | 'status'
  sessionId?: string
  scope?: CoachStreamScope
  content?: string
  toolName?: string
  fullResponse?: string
  reportBlocks?: any[]
  citations?: StreamCitation[]
  // Already localized by the backend; `code` is FORBIDDEN | SESSION_FORBIDDEN | INTERNAL
  error?: string
  code?: string
  transcript?: string
  timestamp: string
  // status event fields
  status?: string
  agent?: string
  tool?: string
  message?: string
  delta?: string
  reasoning?: string
  parallel?: boolean
  agents?: string[]
  inputPreview?: string
  resultPreview?: string
  progress?: { step: number; total: number; label: string }
}

interface AppFloatingChatProps {
  sessionId?: string
  isVisible?: boolean
  onToggle?: (isOpen: boolean) => void
  user?: any
  chatUsers?: any
  token?: string
  usersAssessmentResponseByQuestion?: any
  chatUnreadMessageCount?: number
  saveUserFileAction?: any
  getUsersAssessmentResponseByQuestionAction?: any
  getChatUsersAction?: any
  chatUnreadMessageCountAction?: any
  uploadFileAction?: any
  uploadFileLoading?: boolean
  uploadedFile?: {
    id: string
    name: string
    url: string
  }
  uploadFileRequestId?: string
  uploadedFileRequestId?: string
}

const AppFloatingChatComponent: React.FC<AppFloatingChatProps> = ({
  sessionId,
  isVisible = false,
  onToggle,
  user,
  chatUsers,
  token,
  usersAssessmentResponseByQuestion,
  chatUnreadMessageCount,
  saveUserFileAction,
  getUsersAssessmentResponseByQuestionAction,
  getChatUsersAction,
  chatUnreadMessageCountAction,
  uploadFileAction,
  uploadFileLoading,
  uploadedFile,
  uploadFileRequestId,
  uploadedFileRequestId,
}) => {
  const { t, i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(isVisible)
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [editingTextareaValue, setEditingTextareaValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [activeTab, setActiveTab] = useState<'coach' | 'chat'>('coach')
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)
  const [showSessionList, setShowSessionList] = useState(false)
  const [userMessageCount, setUserMessageCount] = useState(0)
  const [isUserScrolling, setIsUserScrolling] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isHelpInitializing, setIsHelpInitializing] = useState(false)
  // Sessions created locally (e.g. via the top-nav help icon). The history
  // fetch is suppressed for these so the locally-added user message isn't
  // duplicated by the backend round-trip.
  const localOnlySessionsRef = useRef<Set<string>>(new Set())
  // Advisor/admin on a client page: the coach talks about that client's
  // business. Mirrored in a ref so callbacks with older closures still send
  // the current target.
  const { clientId, ready: coachTargetReady } = useCoachTarget()
  const coachTargetRef = useRef(clientId)
  coachTargetRef.current = clientId
  // "What Coach remembers": owners and team members see their business; an
  // advisor or EDO admin only sees it for the client being viewed. Only where
  // Business Memory is rolled out (the panel itself still says when it is off
  // for this person or business).
  const [showMemory, setShowMemory] = useState(false)
  const memoryRolledOut = useFeatureFlag(user?.id || '', 'release-business-memory')
  const roles: string[] = user?.roles ?? []
  const isAdvisorOrAdmin =
    roles.includes(USER_ROLES.BSO_ADVISOR) || roles.includes(USER_ROLES.BSO_ADMIN)
  const canShowMemory = !!memoryRolledOut && (!isAdvisorOrAdmin || !!clientId)
  // The current route, read when a message is sent (callbacks may be older).
  const { pathname } = useLocation()
  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname
  // Scope from the latest 'session' line; drives the advisor-only chip.
  const [streamScope, setStreamScope] = useState<CoachStreamScope | null>(null)
  // On a client page the chip shows before any reply (an opened older thread streams nothing),
  // named from the client's profile, which the client page has already loaded.
  const { data: clientProfile } = useGetBusinessProfileQuery(
    { id: clientId },
    { enabled: !!clientId, refetchOnWindowFocus: false }
  )
  const showAdvisorChip = !!clientId || streamScope?.audience === 'advisor'
  const advisorChipName =
    streamScope?.businessName ||
    (clientId ? clientProfile?.getBusinessProfile?.businessName : undefined)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const floatingChatRef = useRef<HTMLDivElement>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  // Form-related state
  const [processing, setProcessing] = useState(false)
  const [currentStep, setCurrentStep] = useState<{
    stepType?: OnboardingFlowStepTypes
    stepStatus?: OnboardingFlowStepStatus
  }>({})
  const [queuedMessages, setQueuedMessages] = useState<any[]>([])
  const [isUpload, setIsUpload] = useState(false)
  const [uploadedFormFile, setUploadedFormFile] = useState<File[]>([])
  const [startUpload, setStartUpload] = useState(false)

  // Audio-related state
  const [audioSupported, setAudioSupported] = useState(false)

  // Play creation modal state
  const [showCreatePlayModal, setShowCreatePlayModal] = useState(false)
  const [showPlaySuccessModal, setShowPlaySuccessModal] = useState(false)
  const [showPlayErrorModal, setShowPlayErrorModal] = useState(false)
  const [createdUserTask, setCreatedUserTask] = useState<UserTask | null>(null)

  // The store keeps the last upload result (of any file, from any screen)
  // until the next one finishes: remember which one was there when this
  // chat started an upload, so only that upload's result is taken.
  const uploadRequestRef = useRef<string>()

  // Replace pendingFiles and pendingFilePreviews with a single state
  const [pendingFiles, setPendingFiles] = useState<
    {
      file: File
      previewUrl: string
      status: 'pending' | 'uploading' | 'uploaded'
      // The upload this chat started for the file, once it goes up
      uploadId?: string
      uploadedUrl?: string
      uploadedName?: string
    }[]
  >([])
  // The composer's files as they are now. A recording calls back the handler of the render it
  // started in, and the person may take files off, send them or pick others while speaking.
  const pendingFilesRef = useRef(pendingFiles)
  pendingFilesRef.current = pendingFiles

  // Add useForm hook for form handling
  const { handleSubmit, setValue, resetField } = useForm()

  const {
    isSupported: isVoiceSupported,
    isLoading: isVoiceLoading,
    activeKey: activeVoiceMessageKey,
    audioUrl: voiceAudioUrl,
    togglePlayback: toggleVoicePlayback,
    clearPlayback: clearVoicePlayback,
  } = useVoicePlayback()

  // Feature flag to show/hide floating chat (must be called unconditionally with other hooks)
  const showFloatingChat = useFeatureFlag(user?.id || '', 'operational-floating-chat')

  useEffect(() => {
    setAudioSupported(isAudioRecordingSupported())
  }, [])

  // GraphQL hooks
  const { data: chatSessionsData, refetch: refetchChatSessions } = useGetChatSessionsQuery({
    input: {
      userId: user?.id,
      flowType: OnboardingFlowType.GeneralChat,
      ...(clientId && { businessId: clientId }),
    },
  })

  const { mutate: summarySessionTitleMutate } = useSummarySessionTitleMutation({
    onSuccess: () => {
      refetchChatSessions()
    },
  })

  const { mutate: createFlowChatSessionMutate } = useCreateFlowChatSessionMutation({
    onSuccess: () => {
      refetchChatSessions()
    },
  })

  // Add tools query for form handling
  const { data: toolsData, refetch: refetchTools } = useGetAllToolsQuery(
    {
      tenantId: getTenantId(),
    },
    {
      refetchOnWindowFocus: false,
    }
  )
  const tools = (toolsData?.getAllTools?.data as Tool[]) || []

  // Fetch business profile for logo
  const { data: businessData } = useGetBusinessProfileQuery(
    {
      id: user?.id,
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!user?.id,
    }
  )

  // Fetch assignee options and advisor data using the business profile hook
  const { assigneeOptions, advisorsData } = useGetBusinessProfile({
    paramUserId: user?.id,
    enabled: !!user?.id,
  })

  // Get logo from businessData (same logic as AppChatBotFlows)
  const logo = React.useMemo(
    () =>
      businessData?.getBusinessProfile?.logo?.url
        ? businessData?.getBusinessProfile?.logo?.url
        : businessData?.getBusinessProfile?.avatar ?? '',
    [businessData]
  )

  // Fetch the messages with pagination support
  const {
    data: messageHistoryData,
    fetchNextPage,
    hasNextPage,
    isLoading: isMessagesLoading,
    isFetchingNextPage,
    refetch: refetchMessages,
  } = useInfiniteGetMessagesFromBotQuery(
    {
      sessionId: currentSessionId ?? '',
    },
    {
      getNextPageParam: (lastPage: any) => {
        return lastPage.getMessagesFromBot.nextCursor
          ? { cursor: lastPage.getMessagesFromBot.nextCursor }
          : undefined
      },
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      enabled: !!currentSessionId && !localOnlySessionsRef.current.has(currentSessionId ?? ''),
    }
  )

  // Convert message from GraphQL format to component format
  const convertMessage = useCallback(
    (message: {
      __typename?: 'ChatBotMessage'
      role: string
      content: string
      createdAt: string
      isEdited?: boolean | null
      isVoiceMessage?: boolean | null
      voiceDuration?: number | null
      reportBlocks?: string | null
      attachments?: string | null
    }): Message => {
      // Charts / diagrams of an Ask CL answer and the files of a question, stored as JSON
      const reportBlocks = parseStoredJsonArray(message.reportBlocks)
      const attachments = parseStoredJsonArray(message.attachments) as MessageAttachment[]
      const stored = {
        ...(reportBlocks.length > 0 && { reportBlocks }),
        ...(attachments.length > 0 && { attachments }),
      }

      // Check if content is JSON (for form messages)
      if (message?.content?.startsWith('{')) {
        try {
          const content = JSON.parse(message.content)
          if (content.type) {
            return {
              id: `${message.role}-${message.createdAt}`,
              sender: message.role === 'user' ? 'user' : 'bot',
              content,
              timestamp: new Date(message.createdAt),
              createdAt: message.createdAt,
              isEdited: message.isEdited || false,
            }
          }
        } catch (e) {
          console.error('Error parsing message content:', e)
        }
      }

      return {
        id: `${message.role}-${message.createdAt}`,
        sender: message.role === 'user' ? 'user' : 'bot',
        content: message.content ?? '',
        timestamp: new Date(message.createdAt),
        createdAt: message.createdAt,
        isEdited: message.isEdited || false,
        ...(message.isVoiceMessage && {
          isVoiceMessage: true,
          voiceDuration: message.voiceDuration ?? 0,
        }),
        ...stored,
      }
    },
    []
  )

  // Add messages from latest page
  const addMessagesFromLatestPage = useCallback(
    (
      pageMessages: {
        __typename?: 'ChatBotMessage'
        role: string
        content: string
        createdAt: string
        reportBlocks?: string | null
        attachments?: string | null
      }[]
    ) => {
      const newMessages = pageMessages
        // Filter out empty messages from backend, but keep an answer that is only a chart or diagram
        .filter(
          (message) =>
            (message.content && message.content.trim() !== '') ||
            parseStoredJsonArray(message.reportBlocks).length > 0
        )
        .map((message) => convertMessage(message))
        .reverse()
        .filter(Boolean)

      // Avoid adding duplicate messages by checking their createdAt and content
      setMessages((prevMessagesArray) => {
        const existingMessages = new Set(
          prevMessagesArray.map((msg) => `${msg.createdAt}-${msg.content}`)
        )
        const oldMessages =
          newMessages?.filter((msg) => {
            const key = `${msg?.createdAt}-${msg?.content}`
            return !existingMessages.has(key)
          }) ?? []
        return [...oldMessages, ...prevMessagesArray]
      })
    },
    [convertMessage]
  )

  // Track session ID changes to reset messages
  const prevSessionIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (prevSessionIdRef.current !== currentSessionId) {
      // If the sessionId has changed, reset messages and refetch
      setMessages([]) // Clear previous messages
      prevSessionIdRef.current = currentSessionId
    }
  }, [currentSessionId])

  // Switching client (or leaving a client page) starts over, so an advisor
  // never continues one client's thread on another client's page. The
  // composer too: a file or draft meant for one client is never sent into
  // the next client's thread, and an upload still running is not taken.
  const prevCoachTargetRef = useRef(clientId)
  useEffect(() => {
    if (prevCoachTargetRef.current === clientId) return
    prevCoachTargetRef.current = clientId
    setShowMemory(false)
    setCurrentSessionId(null)
    setMessages([])
    setStreamScope(null)
    setUserMessageCount(0)
    setIsUserScrolling(false)
    setPendingFiles([])
    setInputValue('')
    uploadRequestRef.current = undefined
  }, [clientId])

  // Every streaming request body goes through here. `target` is the coach
  // target captured when the request started. The asker's timezone and page
  // help the coach with dates and "this goal"-style questions.
  const buildStreamBody = (fields: Record<string, unknown>, target?: string) =>
    JSON.stringify({
      ...fields,
      ...(target && { targetBusinessId: target }),
      ...streamContextFields(pathnameRef.current, getTenantId()),
    })

  // Handles a 'session' line. Ignored once the advisor has switched client
  // since the request started, so a late reply can't bring back the previous
  // client's thread.
  const applyStreamSession = (data: StreamingResponse, target?: string) => {
    if (target !== coachTargetRef.current) return
    // The message went out without a thread (the last one was refused), so the
    // backend picked this one. Keep the conversation on screen instead of
    // clearing it and reloading that thread's history.
    if (data.sessionId && prevSessionIdRef.current === null) {
      localOnlySessionsRef.current.add(data.sessionId)
      prevSessionIdRef.current = data.sessionId
    }
    setCurrentSessionId(data.sessionId || null)
    if (data.scope) setStreamScope(data.scope)
  }

  // The stored thread can no longer be used (e.g. it belongs to someone else):
  // drop it so the next message starts a fresh thread instead of failing again.
  // The reset effect above is told nothing changed, so the user's message and
  // this error stay on screen.
  const applyStreamError = (data: StreamingResponse) => {
    if (data.code !== 'SESSION_FORBIDDEN') return
    prevSessionIdRef.current = null
    setCurrentSessionId(null)
  }

  // GeneralChat threads an advisor starts on a client page live under that client.
  // `target` is the coach target captured when the request started.
  const generalChatSessionInput = (target?: string) => ({
    flow: OnboardingFlowType.GeneralChat,
    ...(target && { businessId: target }),
  })

  // Handle message history loading
  useEffect(() => {
    if (!messageHistoryData || !currentSessionId) return
    // Skip merge for sessions we just created locally — the streamed
    // user/bot messages are already in state and would duplicate.
    if (localOnlySessionsRef.current.has(currentSessionId)) return

    const pageMessages = messageHistoryData.pages ?? []
    const lastPageMessages =
      pageMessages[pageMessages.length - 1]?.getMessagesFromBot.messages ?? []

    if (!messages.length && pageMessages.length > 1) {
      pageMessages.forEach((page: any) => {
        addMessagesFromLatestPage(page.getMessagesFromBot.messages ?? [])
      })
      return
    }
    if (lastPageMessages.length) {
      addMessagesFromLatestPage(lastPageMessages)
    }
  }, [messageHistoryData, currentSessionId, addMessagesFromLatestPage, messages.length])

  // Auto-scroll to bottom when messages change
  const scrollToBottom = useCallback(
    (force?: boolean) => {
      if (!chatContainerRef.current) return
      if (!isUserScrolling || force) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
      }
    },
    [isUserScrolling]
  )

  // Handle user scroll for pagination
  const handleUserScroll = () => {
    if (chatContainerRef.current) {
      const isAtBottom =
        chatContainerRef.current.scrollHeight - chatContainerRef.current.scrollTop ===
        chatContainerRef.current.clientHeight
      setIsUserScrolling(!isAtBottom)

      // Trigger loading more messages when the user scrolls to the top
      if (chatContainerRef.current.scrollTop === 0 && hasNextPage) {
        fetchNextPage()
      }
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping, scrollToBottom])

  // Initialize with greeting message for new sessions. Skip while the
  // top-nav help flow is bootstrapping its own session — we don't want the
  // greeting to flash before the report message lands.
  useEffect(() => {
    if (isOpen && messages.length === 0 && !currentSessionId && !isHelpInitializing) {
      setMessages([
        {
          id: 'greeting',
          sender: 'bot',
          content: t('askCL.initialMessage'),
          timestamp: new Date(),
        },
      ])
    }
  }, [isOpen, messages.length, currentSessionId, t, isHelpInitializing])

  const toggleChat = useCallback(() => {
    const newState = !isOpen
    setIsOpen(newState)
    // Reset expanded state when closing the chat
    if (!newState) {
      setIsExpanded(false)
    }
    onToggle?.(newState)
  }, [isOpen, onToggle])

  // Handle expand/collapse
  const toggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded)
  }, [isExpanded])

  // Handle folder icon click
  const handleFolderClick = useCallback(() => {
    setShowSessionList(!showSessionList)
    if (!showSessionList) {
      refetchChatSessions()
    }
  }, [showSessionList, refetchChatSessions])

  // Handle session selection
  const handleSessionSelect = useCallback((session: any) => {
    // If the user explicitly re-opens a session that we'd marked local-only,
    // they want backend history this time — clear the suppression flag.
    localOnlySessionsRef.current.delete(session.id)
    setCurrentSessionId(session.id)
    setMessages([]) // Clear messages - they will be loaded by the message history effect
    setShowSessionList(false)
    setIsUserScrolling(false)

    // Reset message count for existing session (we don't know how many messages it has)
    // For existing sessions, we assume they might already have a title, so we mark them as titled
    setUserMessageCount(0)
  }, [])

  // How a report card sends a message as if the person typed it (e.g. the Google Drive files they
  // picked). Set below, where sendToCoach exists; the ref keeps the cards' context the same object.
  const sendCardMessageRef = useRef<(text: string) => boolean>(() => false)

  // The site the person came back from without connecting it (coachConnect=failed), and the
  // thread they left from: that thread's Connect card says so
  const [connectFailed, setConnectFailed] = useState<{
    provider: string
    sessionId: string
  } | null>(null)

  // The open thread, for report cards that send the person to another site and back, and the way
  // they send a message in it
  const coachChat = React.useMemo(
    () => ({
      sessionId: currentSessionId,
      sendMessage: (text: string) => sendCardMessageRef.current(text),
      connectFailed:
        connectFailed?.sessionId === currentSessionId ? connectFailed?.provider : undefined,
    }),
    [currentSessionId, connectFailed]
  )

  // Back from connecting Canva or Google Drive: the Connect card sent the person there with a
  // flash naming the thread they were in (coachReturn, never connectorName: older screens read
  // that as a tool just connected and record it again). Open the chat on the Coach tab, in that
  // thread, once the coach target is known: on a client page it comes a moment later, and the
  // client-switch reset above would drop a thread opened before it. Without a thread, nothing
  // pops open. coachConnect=failed: the person declined, or the sign-in failed.
  const { flashParams, removeFlashParams } = useFlashParams<{
    coachReturn?: string
    coachSession?: string
    coachConnect?: string
  }>()
  useEffect(() => {
    const coachReturn = flashParams?.coachReturn
    if ((coachReturn !== 'canva' && coachReturn !== 'googleDrive') || !coachTargetReady) return
    if (isCoachSessionId(flashParams.coachSession)) {
      setActiveTab('coach')
      setIsOpen(true)
      onToggle?.(true)
      handleSessionSelect({ id: flashParams.coachSession })
      setConnectFailed(
        flashParams.coachConnect === 'failed'
          ? { provider: coachReturn, sessionId: flashParams.coachSession }
          : null
      )
    }
    removeFlashParams(['coachReturn', 'coachSession', 'coachConnect'])
  }, [
    flashParams?.coachReturn,
    flashParams?.coachSession,
    flashParams?.coachConnect,
    coachTargetReady,
  ])

  // Send an empty message to bot to trigger initial greeting
  const sendEmptyMessageToBot = useCallback(
    async (sessionId: string) => {
      if (!sessionId) return

      try {
        const streamChatbotUrl = process.env.REACT_APP_STREAM_CHATBOT_URL || ''
        const authToken = await getAuthToken()

        if (!authToken) {
          throw new Error('Authentication token is required')
        }

        console.log('Sending empty message to initialize chat session:', sessionId)

        const target = coachTargetRef.current
        const response = await fetch(streamChatbotUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: buildStreamBody(
            {
              userMessage: '', // Empty message to trigger bot greeting
              sessionId: sessionId,
            },
            target
          ),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        // Read the streaming response to completion
        const reader = response.body?.getReader()
        let streamError = ''
        if (reader) {
          const decoder = new TextDecoder()
          let buffer = ''

          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
              if (line.trim()) {
                try {
                  const data: StreamingResponse = JSON.parse(line)
                  console.log('Received streaming data from empty message:', data)
                  if (data.type === 'session') {
                    applyStreamSession(data, target)
                  } else if (data.type === 'error') {
                    applyStreamError(data)
                    streamError = data.error || t('askCL.errorRetry')
                  }
                } catch (e) {
                  console.error('Error parsing empty message streaming response:', e)
                }
              }
            }
          }
        }

        // Nothing was saved, so there is no greeting to fetch: show the reason.
        if (streamError) {
          setMessages((prev) => [
            ...prev,
            {
              id: `error-${Date.now()}`,
              sender: 'bot',
              content: streamError,
              timestamp: new Date(),
            },
          ])
          return
        }

        // After the empty message is processed, refetch messages to get the bot's greeting
        console.log('Empty message sent successfully, refetching messages...')
        setTimeout(() => {
          refetchMessages()
        }, 500) // Small delay to ensure backend has processed the message
      } catch (error) {
        console.error('Error sending empty message to bot:', error)
        // Add a fallback greeting message
        const greetingMessage: Message = {
          id: `greeting-${Date.now()}`,
          sender: 'bot',
          content: t('askCL.fallbackGreeting'),
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, greetingMessage])
      }
    },
    [refetchMessages]
  )

  // Handle new conversation
  const handleNewConversation = useCallback(() => {
    if (!user?.id) return

    // Create a new GeneralChat session
    const target = coachTargetRef.current
    createFlowChatSessionMutate(
      {
        input: generalChatSessionInput(target),
      },
      {
        onSuccess: async (response: any) => {
          // The advisor switched client meanwhile: this thread belongs to the previous one.
          if (target !== coachTargetRef.current) return
          const newSessionId = response.createFlowChatSession.id
          console.log('Created new GeneralChat session:', newSessionId)

          // Set the session ID and reset state
          setCurrentSessionId(newSessionId)
          setMessages([]) // Clear messages, bot greeting will come from empty message
          setShowSessionList(false)
          setUserMessageCount(0)
          setIsUserScrolling(false)

          // Send empty message to trigger bot greeting
          await sendEmptyMessageToBot(newSessionId)
        },
        onError: (error: any) => {
          console.error('Error creating new chat session:', error)
          // Still close the session list and show error
          setShowSessionList(false)
        },
      }
    )
  }, [user?.id, createFlowChatSessionMutate, sendEmptyMessageToBot])

  // Handle suggestion click. `overrideSessionId` lets callers (e.g. the
  // top-nav help flow) target a freshly-created session synchronously,
  // before React state has settled. When provided, the in-flight typing
  // guard is bypassed — the new session is independent of any prior
  // stream still landing in the previous session.
  const handleSuggestionClick = useCallback(
    async (
      suggestion: string,
      overrideSessionId?: string | null,
      forceEffort?: 'minimal' | 'low' | 'medium' | 'high'
    ) => {
      if (!user?.id) return
      if (!overrideSessionId && isTyping) return

      // Keep the input empty — the suggestion is already shown as a sent
      // message bubble, and echoing it into the textbox while the bot
      // streams a response is confusing (especially when triggered
      // programmatically by the Help icon).
      setInputValue('')

      // Create user message
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        sender: 'user',
        content: suggestion,
        timestamp: new Date(),
      }

      setMessages((prev) => [...prev, userMessage])
      setIsTyping(true)

      // Increment user message count
      const newMessageCount = userMessageCount + 1
      setUserMessageCount(newMessageCount)

      try {
        // Call the streaming Lambda Function URL
        const streamChatbotUrl = process.env.REACT_APP_STREAM_CHATBOT_URL || ''
        const authToken = await getAuthToken()

        if (!authToken) {
          throw new Error('Authentication token is required')
        }

        const target = coachTargetRef.current
        const response = await fetch(streamChatbotUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: buildStreamBody(
            {
              userMessage: suggestion,
              sessionId: overrideSessionId ?? currentSessionId,
              ...(forceEffort && { forceEffort }),
            },
            target
          ),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('No response body reader available')
        }

        let botMessage: Message = {
          id: `bot-${Date.now()}`,
          sender: 'bot',
          content: '',
          timestamp: new Date(),
        }

        // Add the empty bot message immediately
        setMessages((prev) => [...prev, botMessage])

        const decoder = new TextDecoder()
        let buffer = ''
        let fullContent = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.trim()) {
              try {
                const data: StreamingResponse = JSON.parse(line)

                if (data.type === 'session') {
                  applyStreamSession(data, target)
                } else if (data.type === 'content') {
                  fullContent += data.content || ''
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                    )
                  )
                } else if (data.type === 'complete') {
                  setMessages((prev) =>
                    applyStreamComplete(prev, botMessage.id, data, { text: fullContent })
                  )
                } else if (data.type === 'error') {
                  applyStreamError(data)
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === botMessage.id
                        ? {
                            ...msg,
                            content: data.error || t('askCL.errorRetry'),
                          }
                        : msg
                    )
                  )
                }
              } catch (e) {
                console.error('Error parsing streaming response:', e)
              }
            }
          }
        }
      } catch (error) {
        console.error('Error sending suggestion message:', error)
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          sender: 'bot',
          content: t('askCL.errorRetry'),
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, errorMessage])
      } finally {
        setIsTyping(false)
        setInputValue('') // Clear the input
      }
    },
    [user?.id, isTyping, currentSessionId, userMessageCount]
  )

  // Keep a ref to the latest handleSuggestionClick so the help-event handler
  // (registered once) doesn't capture a stale closure.
  const handleSuggestionClickRef = useRef(handleSuggestionClick)
  useEffect(() => {
    handleSuggestionClickRef.current = handleSuggestionClick
  })

  // Listen for external requests to open the chat with a quick-suggestion
  // (e.g. the "Get help" icon in the top navbar dispatches this event).
  // Always start a fresh GeneralChat session so the support request lives
  // in its own thread instead of polluting the user's prior conversation.
  useEffect(() => {
    const handler = (event: Event) => {
      if (!user?.id) return
      const detail = (
        event as CustomEvent<{
          suggestionKey?: string
          forceEffort?: 'minimal' | 'low' | 'medium' | 'high'
        }>
      ).detail
      const suggestion = detail?.suggestionKey
        ? t(detail.suggestionKey)
        : t('askCL.suggestions.report')
      const forceEffort = detail?.forceEffort

      setActiveTab('coach')
      setIsOpen(true)
      onToggle?.(true)
      setIsHelpInitializing(true)
      // Wipe any prior greeting/messages immediately so the loading state
      // is the only thing visible while the session is being created.
      setMessages([])

      const target = coachTargetRef.current
      createFlowChatSessionMutate(
        { input: generalChatSessionInput(target) },
        {
          onSuccess: async (response: any) => {
            // The advisor switched client meanwhile: this thread belongs to the previous one.
            if (target !== coachTargetRef.current) {
              setIsHelpInitializing(false)
              return
            }
            const newSessionId = response.createFlowChatSession.id
            localOnlySessionsRef.current.add(newSessionId)
            setCurrentSessionId(newSessionId)
            setMessages([])
            setShowSessionList(false)
            setUserMessageCount(0)
            setIsUserScrolling(false)
            try {
              await handleSuggestionClickRef.current(suggestion, newSessionId, forceEffort)
            } finally {
              setIsHelpInitializing(false)
            }
          },
          onError: (error: any) => {
            console.error('Error creating help chat session:', error)
            setIsHelpInitializing(false)
          },
        }
      )
    }
    window.addEventListener(OPEN_COACH_LOGIC_HELP_EVENT, handler)
    return () => window.removeEventListener(OPEN_COACH_LOGIC_HELP_EVENT, handler)
  }, [onToggle, t, user?.id, createFlowChatSessionMutate])

  // Auto-generate title after 4 user messages (only once per session)
  const checkAndGenerateTitle = useCallback(
    (sessionId: string, messageCount: number) => {
      if (messageCount <= 6 && sessionId) {
        summarySessionTitleMutate({ id: sessionId })
        // Mark this session as titled to prevent future calls
      }
    },
    [summarySessionTitleMutate]
  )

  // Group sessions by date
  const groupSessionsByDate = useCallback((sessions: any[]) => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    const groups: Record<string, any[]> = {}

    sessions.forEach((session) => {
      if (!session.createdAt) return

      const sessionDate = new Date(session.createdAt)
      const sessionDateString = sessionDate.toDateString()
      const todayString = today.toDateString()
      const yesterdayString = yesterday.toDateString()

      let groupKey: string
      if (sessionDateString === todayString) {
        groupKey = t('askCL.today')
      } else if (sessionDateString === yesterdayString) {
        groupKey = t('askCL.yesterday')
      } else {
        // Format date in the active locale (e.g. "26 abr 2025" in Spanish)
        groupKey = sessionDate.toLocaleDateString(i18n.language, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      }

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(session)
    })

    return groups
  }, [])

  // Filter sessions based on search query
  const filteredSessions = React.useMemo(() => {
    const sessions = chatSessionsData?.getChatSessions || []
    if (!searchQuery.trim()) {
      return sessions
    }
    return sessions.filter((session: any) =>
      (session.title || t('askCL.generalChat')).toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [chatSessionsData?.getChatSessions, searchQuery])

  // Group filtered sessions by date
  const groupedSessions = React.useMemo(() => {
    return groupSessionsByDate(filteredSessions)
  }, [filteredSessions, groupSessionsByDate])

  // Close chat when clicking outside - disabled to allow modal interactions
  // useEffect(() => {
  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (floatingChatRef.current && !floatingChatRef.current.contains(event.target as Node)) {
  //       if (isOpen) {
  //         toggleChat()
  //       }
  //     }
  //   }

  //   if (isOpen) {
  //     document.addEventListener('mousedown', handleClickOutside)
  //   }

  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside)
  //   }
  // }, [isOpen, toggleChat])

  /**
   * Sends one message, typed or spoken, to the coach and shows the streamed reply: progress and
   * thoughts while it works, then the text or form, its charts and sources, the thread's title and
   * any error. `userBubble` is the user's message as shown; `fields` are the request's own.
   */
  const sendToCoach = useCallback(
    (userBubble: Message, fields: Record<string, unknown>) => {
      setMessages((prev) => [...prev, userBubble])
      setProcessing(true)

      // Use streaming endpoint for all messages (handles both text and structured responses)
      ;(async () => {
        try {
          const streamChatbotUrl = process.env.REACT_APP_STREAM_CHATBOT_URL || ''
          const authToken = await getAuthToken()
          if (!authToken) throw new Error('Authentication token is required')

          const target = coachTargetRef.current
          const response = await fetch(streamChatbotUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: authToken,
            },
            body: buildStreamBody({ ...fields, sessionId: currentSessionId }, target),
          })

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const reader = response.body?.getReader()
          if (!reader) {
            throw new Error('No response body reader available')
          }

          let botMessage: Message = {
            id: `bot-${Date.now()}`,
            sender: 'bot',
            content: '',
            timestamp: new Date(),
          }
          setMessages((prev) => [...prev, botMessage])

          const decoder = new TextDecoder()
          let buffer = ''
          let fullContent = ''
          let isStructuredResponse = false
          let structuredContent: any = null
          // The thread of this message, from the 'session' line. Its title is
          // made on 'complete': the 'session' line comes before the message is
          // saved, and a title made then would miss the question.
          let titleSessionId: string | undefined

          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
              if (line.trim()) {
                try {
                  const data: StreamingResponse = JSON.parse(line)

                  if (data.type === 'session') {
                    applyStreamSession(data, target)
                    titleSessionId = data.sessionId || titleSessionId
                  } else if (data.type === 'status') {
                    // reasoning_token: accumulate the model's chain-of-thought
                    // onto the bot message. Rendered in a collapsible Thoughts
                    // panel above the answer; doesn't touch status text.
                    if (data.status === 'reasoning_token' && data.delta) {
                      const delta = data.delta
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === botMessage.id
                            ? { ...msg, reasoningText: (msg.reasoningText || '') + delta }
                            : msg
                        )
                      )
                      continue
                    }

                    // Skip granular events that don't add UI value:
                    //  - agent_complete / tool_end: redundant with next agent_running
                    //  - planner_decision: shown via subsequent agent_running events
                    const skipStatuses = new Set(['agent_complete', 'tool_end', 'planner_decision'])
                    if (skipStatuses.has(data.status || '')) continue

                    // Show agent progress only while no real content has arrived
                    if (fullContent) continue

                    // Agents with a label of their own (askCL.status.agents.<agent>)
                    const knownAgents = [
                      'goal_management_agent',
                      'system_report_agent',
                      'research_agent',
                      'market_research_agent',
                      'meeting_context_agent',
                      'text2sql_agent',
                      'progress_analytics_agent',
                      'user_business_management_agent',
                      'handoff_agent',
                      'query_user_agent',
                      'update_user_agent',
                      'platform_data_agent',
                      'data_discovery_agent',
                      'planner_agent',
                    ]

                    // Prefer the backend-supplied message (already friendly),
                    // then agent label, then tool label, then status fallback.
                    let statusText = data.message
                    if (
                      !statusText &&
                      data.status === 'agent_running' &&
                      data.agent &&
                      knownAgents.includes(data.agent)
                    ) {
                      statusText = t(`askCL.status.agents.${data.agent}`)
                    }
                    if (!statusText && data.status === 'tool_start' && data.tool) {
                      statusText = t('askCL.status.runningTool', {
                        tool: data.tool.replace(/[-_]/g, ' '),
                      })
                    }
                    if (!statusText) {
                      const statusLabels: Record<string, string> = {
                        thinking: t('askCL.status.thinking'),
                        executing: t('askCL.status.working'),
                        agent_running: t('askCL.status.working'),
                      }
                      statusText =
                        (data.status && statusLabels[data.status]) || t('askCL.status.default')
                    }

                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === botMessage.id ? { ...msg, content: `_${statusText}_` } : msg
                      )
                    )
                  } else if (data.type === 'content') {
                    // Check if content is structured (JSON) or plain text
                    const content = data.content || ''

                    // Try to parse as JSON first (structured response)
                    try {
                      const parsedContent = JSON.parse(content)
                      if (parsedContent.content && typeof parsedContent.content === 'object') {
                        // This is a structured response (form/UI component)
                        isStructuredResponse = true
                        structuredContent = parsedContent
                        setMessages((prev) =>
                          prev.map((msg) =>
                            msg.id === botMessage.id
                              ? { ...msg, content: parsedContent.content }
                              : msg
                          )
                        )
                      } else {
                        // Parsed but not structured, treat as text
                        fullContent += content
                        setMessages((prev) =>
                          prev.map((msg) =>
                            msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                          )
                        )
                      }
                    } catch (parseError) {
                      // Not JSON, treat as regular text content
                      fullContent += content
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                        )
                      )
                    }
                  } else if (data.type === 'complete') {
                    // Keep structured content as is; otherwise the complete response or
                    // accumulated content, with the answer's charts and sources
                    const structured =
                      isStructuredResponse && structuredContent
                        ? structuredContent.content
                        : undefined
                    setMessages((prev) =>
                      applyStreamComplete(prev, botMessage.id, data, {
                        text: fullContent,
                        structured,
                      })
                    )
                    // Both the question and the answer are saved by now. Once per
                    // message (a handed-off flow can send several 'complete' lines).
                    if (titleSessionId) {
                      checkAndGenerateTitle(titleSessionId, userMessageCount + 1)
                      titleSessionId = undefined
                    }

                    // Handle TTS with transcript if available
                  } else if (data.type === 'error') {
                    applyStreamError(data)
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === botMessage.id
                          ? {
                              ...msg,
                              content: data.error || t('askCL.errorRetry'),
                            }
                          : msg
                      )
                    )
                  }
                } catch (e) {
                  console.error('Error parsing streaming response:', e, 'Line:', line)
                }
              }
            }
          }

          // Update user message count after successful response
          setUserMessageCount(userMessageCount + 1)
        } catch (error) {
          console.error('Error sending message:', error)
          const errorMessage: Message = {
            id: `error-${Date.now()}`,
            sender: 'bot',
            content: t('askCL.errorRetry'),
            timestamp: new Date(),
          }
          setMessages((prev) => [...prev, errorMessage])
        } finally {
          setProcessing(false)
        }
      })()
    },
    [currentSessionId, userMessageCount, checkAndGenerateTitle, t]
  )

  // On Send, for each pending file, dispatch uploadFileAction (no callback, no Promise)
  const handleSendMessage = useCallback(() => {
    if (isTyping || processing || !user?.id) return
    const messageToSend = inputValue.trim()

    // If any file is uploading, do nothing
    if (pendingFiles.some((pf) => pf.status === 'uploading')) return

    // If there are files with status 'pending', start upload
    if (pendingFiles.some((pf) => pf.status === 'pending')) {
      const idx = pendingFiles.findIndex((pf) => pf.status === 'pending')
      if (idx !== -1) {
        const newPendingFiles = [...pendingFiles]
        uploadRequestRef.current = nextUploadId()
        newPendingFiles[idx] = {
          ...newPendingFiles[idx],
          status: 'uploading',
          uploadId: uploadRequestRef.current,
        }
        uploadFileAction({
          files: [newPendingFiles[idx].file],
          isMultiple: false,
          saveFile: true,
          requestId: uploadRequestRef.current,
        })
        setPendingFiles(newPendingFiles)
      }
      return
    }

    // If all files are uploaded or no files, send the message
    const { fileLinks, attachments } = uploadedFilesOf(pendingFiles)
    let finalMessage = messageToSend
    if (fileLinks) {
      finalMessage = finalMessage ? `${finalMessage}\n${fileLinks}` : fileLinks
    }

    setInputValue('')
    setPendingFiles([])
    sendToCoach(
      { id: `user-${Date.now()}`, sender: 'user', content: finalMessage, timestamp: new Date() },
      {
        userMessage: finalMessage,
        userMessageType: fileLinks ? 'file' : 'text',
        ...(attachments.length > 0 && { attachments }),
      }
    )
  }, [isTyping, processing, user?.id, inputValue, pendingFiles, uploadFileAction, sendToCoach])

  // Check if current step requires form input
  const lastMessageContent = messages[messages.length - 1]?.content
  const isFormStep =
    currentStep.stepType === OnboardingFlowStepTypes.Form ||
    (typeof lastMessageContent === 'object' &&
      lastMessageContent?.type === OnboardingFlowStepTypes.Form)
  const isToolConnectionStep =
    currentStep.stepType === OnboardingFlowStepTypes.ToolConnection ||
    (typeof lastMessageContent === 'object' &&
      lastMessageContent?.type === OnboardingFlowStepTypes.ToolConnection)

  // A message from a report card goes out as a typed one would, when one could be typed: not
  // while a guided flow waits for its form or a tool connection, which would take the message as
  // its answer (the composer's own draft and files stay where they are). Kept current for the
  // cards' context.
  useEffect(() => {
    sendCardMessageRef.current = (text: string) => {
      const message = text.trim()
      if (!message || isTyping || processing || isFormStep || isToolConnectionStep || !user?.id) {
        return false
      }
      sendToCoach(
        { id: `user-${Date.now()}`, sender: 'user', content: message, timestamp: new Date() },
        { userMessage: message, userMessageType: 'text' }
      )
      return true
    }
  })

  // When the upload this chat started (Enter, or the upload box) finishes, the
  // files it was for get their links. Only its own result counts: the store
  // also holds other screens' uploads and file saves. An upload that ended
  // without its files, or that another upload replaced (one runs at a time),
  // puts them back to pending, so the next Enter tries again; a failed one
  // (the store gets an empty list) also tells the person. Its result goes
  // only to the files that went up in it: any other file still waiting was
  // in an earlier upload that this one replaced, so it goes back to pending.
  useEffect(() => {
    const waiting = pendingFiles.filter((pf) => pf.status === 'uploading' && !pf.uploadedUrl)
    const ours = uploadRequestRef.current
    if (waiting.length === 0 || !ours) return
    const replaced = uploadFileRequestId !== ours
    if (!replaced && uploadedFileRequestId !== ours) return
    // The files that went up in this upload, in the order they went
    const sent = waiting.filter((pf) => pf.uploadId === ours)
    const results = replaced ? undefined : uploadResultsOf(uploadedFile, sent.length)
    if (!replaced && Array.isArray(uploadedFile) && uploadedFile.length === 0) {
      showErrorToast(t('files.uploadFailed'))
    }
    uploadRequestRef.current = undefined
    setPendingFiles((prev) =>
      prev.map((pf) => {
        if (!waiting.includes(pf)) return pf
        const result = results?.[sent.indexOf(pf)]
        return result
          ? { ...pf, status: 'uploaded', uploadedUrl: result.url, uploadedName: result.name }
          : { ...pf, status: 'pending' }
      })
    )
  }, [uploadedFile, uploadedFileRequestId, uploadFileRequestId, pendingFiles, t])

  // One of the composer's files is going up. The upload box waits for it: one upload runs at a
  // time, and the store can say none is running while it does (another screen's upload field
  // resets the store when it closes).
  const composerUploading = pendingFiles.some((pf) => pf.status === 'uploading')

  // Custom renderers for Markdown inside user-message bubble (purple bg → white text)
  const WhiteParagraph = (props: any) => <p className="text-white mb-2">{props.children}</p>
  const WhiteUl = (props: any) => (
    <ul className="list-disc ml-4 mb-2 text-white">{props.children}</ul>
  )
  const WhiteOl = (props: any) => (
    <ol className="list-decimal ml-4 mb-2 text-white">{props.children}</ol>
  )
  const WhiteLi = (props: any) => <li className="mb-1 text-white">{props.children}</li>
  const WhiteStrong = (props: any) => (
    <strong className="font-semibold text-white">{props.children}</strong>
  )
  const WhiteEm = (props: any) => <em className="italic text-white">{props.children}</em>
  const WhiteH1 = (props: any) => (
    <h1 className="text-2xl font-bold mb-4 text-white">{props.children}</h1>
  )
  const WhiteH2 = (props: any) => (
    <h2 className="text-xl font-bold mb-3 text-white">{props.children}</h2>
  )
  const WhiteH3 = (props: any) => (
    <h3 className="text-lg font-bold mb-2 text-white">{props.children}</h3>
  )

  // Form submission handler using streaming endpoint
  const submitDataConfirmationForm = useCallback(
    async (formData: any) => {
      const filesFields = ['businessPlanFile']
      const filesToUpload = filesFields.filter((field) => formData[field])

      if (filesToUpload.length > 0) {
        filesToUpload.forEach((field) => {
          // Check if formData[field] is an array
          const files = Array.isArray(formData[field]) ? formData[field] : [formData[field]]
          uploadFileAction({
            files: files as unknown as FileList,
            isMultiple: files.length > 1, // Set isMultiple based on file count
            saveFile: true,
          })
        })
        return
      }

      setProcessing(true)
      const { id, ...data } = formData

      // Detect if this is a SMART goal template payload
      const isGoalTemplatePayload =
        typeof data === 'object' &&
        data !== null &&
        'goalName' in data &&
        'smartHeading' in data &&
        'category' in data

      // Use streaming endpoint for form submission
      ;(async () => {
        try {
          const streamChatbotUrl = process.env.REACT_APP_STREAM_CHATBOT_URL || ''
          const authToken = await getAuthToken()
          if (!authToken) throw new Error('Authentication token is required')

          const target = coachTargetRef.current
          const response = await fetch(streamChatbotUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: authToken,
            },
            body: buildStreamBody(
              {
                userMessage: JSON.stringify(data),
                userMessageType: isGoalTemplatePayload ? 'text' : 'formResponse',
                ...(formData.skip ? { skip: true } : {}),
                sessionId: currentSessionId,
              },
              target
            ),
          })

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const reader = response.body?.getReader()
          if (!reader) {
            throw new Error('No response body reader available')
          }

          let botMessage: Message = {
            id: `bot-${Date.now()}`,
            sender: 'bot',
            content: '',
            timestamp: new Date(),
          }
          setMessages((prev) => [...prev, botMessage])

          const decoder = new TextDecoder()
          let buffer = ''
          let fullContent = ''
          let isStructuredResponse = false
          let structuredContent: any = null

          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() || ''

            for (const line of lines) {
              if (line.trim()) {
                try {
                  const streamData: StreamingResponse = JSON.parse(line)

                  if (streamData.type === 'session') {
                    applyStreamSession(streamData, target)
                  } else if (streamData.type === 'content') {
                    // Check if content is structured (JSON) or plain text
                    const content = streamData.content || ''

                    // Try to parse as JSON first (structured response)
                    try {
                      const parsedContent = JSON.parse(content)
                      if (parsedContent.content && typeof parsedContent.content === 'object') {
                        // This is a structured response (form/UI component)
                        isStructuredResponse = true
                        structuredContent = parsedContent
                        setMessages((prev) =>
                          prev.map((msg) =>
                            msg.id === botMessage.id
                              ? { ...msg, content: parsedContent.content }
                              : msg
                          )
                        )
                      } else {
                        // Parsed but not structured, treat as text
                        fullContent += content
                        setMessages((prev) =>
                          prev.map((msg) =>
                            msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                          )
                        )
                      }
                    } catch (parseError) {
                      // Not JSON, treat as regular text content
                      fullContent += content
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                        )
                      )
                    }
                  } else if (streamData.type === 'complete') {
                    const structured =
                      isStructuredResponse && structuredContent
                        ? structuredContent.content
                        : undefined
                    setMessages((prev) =>
                      applyStreamComplete(prev, botMessage.id, streamData, {
                        text: fullContent,
                        structured,
                      })
                    )
                  } else if (streamData.type === 'error') {
                    applyStreamError(streamData)
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === botMessage.id
                          ? {
                              ...msg,
                              content: streamData.error || t('askCL.errorRetry'),
                            }
                          : msg
                      )
                    )
                  }
                } catch (e) {
                  console.error('Error parsing form response stream:', e, 'Line:', line)
                }
              }
            }
          }
        } catch (error) {
          console.error('Error sending form data:', error)
          const errorMessage: Message = {
            id: `error-${Date.now()}`,
            sender: 'bot',
            content: t('askCL.errorFormRetry'),
            timestamp: new Date(),
          }
          setMessages((prev) => [...prev, errorMessage])
        } finally {
          setProcessing(false)
        }
      })()
    },
    [currentSessionId, uploadFileAction]
  )

  // Handle audio recording completion: the words go to the coach as a typed message would.
  const handleAudioRecording = useCallback(
    (audioBlob: Blob, transcript?: string, duration?: number) => {
      if (!audioBlob || isTyping || processing || !user?.id) return
      const spoken = transcript?.trim()
      if (!spoken) {
        showErrorToast(t('askCL.voice.notHeard'))
        return
      }

      // A guided flow's form on screen: the words may answer it.
      const lastMessageContent = messages[messages.length - 1]?.content
      const isFormContext =
        typeof lastMessageContent === 'object' &&
        lastMessageContent?.type === OnboardingFlowStepTypes.Form

      // Files already uploaded go along, as with a typed message: those in the composer when the
      // recording stops. Only they leave it; a file still going up stays for the next message.
      const { uploaded, fileLinks, attachments } = uploadedFilesOf(pendingFilesRef.current)
      const text = fileLinks ? `${spoken}\n${fileLinks}` : spoken
      if (uploaded.length > 0) {
        setPendingFiles((prev) => prev.filter((pf) => !uploaded.includes(pf)))
      }

      sendToCoach(
        {
          id: `user-${Date.now()}`,
          sender: 'user',
          content: spoken,
          timestamp: new Date(),
          isVoiceMessage: true,
          voiceDuration: duration || 0,
        },
        {
          userMessage: text,
          userMessageType: isFormContext ? 'AudioToForm' : 'Audio',
          audioTranscript: text,
          isVoiceMessage: true,
          voiceDuration: duration || 0,
          ...(attachments.length > 0 && { attachments }),
        }
      )
    },
    [isTyping, processing, user?.id, messages, sendToCoach, t]
  )

  // Handle new message from bot response
  const handleNewMessage = useCallback((message: any) => {
    setCurrentStep(message)
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        id: `bot-${Date.now()}`,
        sender: 'bot',
        content: message.content,
        timestamp: new Date(),
      },
    ])
    setIsUserScrolling(false)
    setProcessing(false)
  }, [])

  // Handle typing animation callback
  const handleTypingMessage = useCallback(
    (text: string, displayText: string) => {
      if (text === displayText) {
        if (queuedMessages.length && text) {
          const firstQueueMessage = queuedMessages.shift()
          handleNewMessage(firstQueueMessage!)
          setQueuedMessages([...queuedMessages])
          return
        }
      } else {
        if (text?.length) {
        }
      }
      if (displayText.length) {
        scrollToBottom()
      }
    },
    [queuedMessages, handleNewMessage, scrollToBottom]
  )

  // Handle created user task for play creation success/error tracking
  const handleSetCreatedUserTask: React.Dispatch<React.SetStateAction<UserTask>> = (value) => {
    if (typeof value === 'function') {
      setCreatedUserTask((prev) => value(prev!))
    } else {
      setCreatedUserTask(value)
    }
  }

  // Handle cancel edit
  const handleCancelEdit = useCallback(() => {
    // Remove the editing message from the messages array
    setMessages((prev) => prev.filter((msg) => !msg.isEditingMessage))
    setEditingTextareaValue('')
    setInputValue('')
  }, [])

  // Handle save edit
  const handleSaveEdit = useCallback(() => {
    const trimmedText = (editingTextareaValue || '').trim()
    if (!trimmedText) {
      handleCancelEdit()
      return
    }

    // Find the editing message
    const editingMessageIndex = messages.findIndex((msg) => msg.isEditingMessage)
    if (editingMessageIndex === -1) return

    const editingMessage = messages[editingMessageIndex]
    const originalMessageIndex = editingMessage.editingOriginalMessageIndex

    if (originalMessageIndex === undefined) return

    // Remove the editing message (don't update the original)
    setMessages((prev) => prev.filter((msg) => !msg.isEditingMessage))

    // Add the edited user message as a new message
    setMessages((prev) => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        sender: 'user',
        content: trimmedText,
        timestamp: new Date(),
        isEdited: true,
      },
    ])

    // Set processing state
    setProcessing(true)

    // Send via streaming endpoint
    ;(async () => {
      try {
        const streamChatbotUrl = process.env.REACT_APP_STREAM_CHATBOT_URL || ''
        const authToken = await getAuthToken()
        if (!authToken) throw new Error('Authentication token is required')

        const target = coachTargetRef.current
        const response = await fetch(streamChatbotUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: buildStreamBody(
            {
              userMessage: trimmedText,
              userMessageType: 'text',
              sessionId: currentSessionId,
              isEditingLastMessage: true,
            },
            target
          ),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const reader = response.body?.getReader()
        if (!reader) {
          throw new Error('No response body reader available')
        }

        let botMessage: Message = {
          id: `bot-${Date.now()}`,
          sender: 'bot',
          content: '',
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, botMessage])

        const decoder = new TextDecoder()
        let buffer = ''
        let fullContent = ''
        let isStructuredResponse = false
        let structuredContent: any = null

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.trim()) {
              try {
                const data: StreamingResponse = JSON.parse(line)

                if (data.type === 'session') {
                  applyStreamSession(data, target)
                } else if (data.type === 'content') {
                  const content = data.content || ''

                  try {
                    const parsedContent = JSON.parse(content)
                    if (parsedContent.content && typeof parsedContent.content === 'object') {
                      isStructuredResponse = true
                      structuredContent = parsedContent
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === botMessage.id
                            ? { ...msg, content: parsedContent.content }
                            : msg
                        )
                      )
                    } else {
                      fullContent += content
                      setMessages((prev) =>
                        prev.map((msg) =>
                          msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                        )
                      )
                    }
                  } catch {
                    fullContent += content
                    setMessages((prev) =>
                      prev.map((msg) =>
                        msg.id === botMessage.id ? { ...msg, content: fullContent } : msg
                      )
                    )
                  }
                } else if (data.type === 'complete') {
                  const structured =
                    isStructuredResponse && structuredContent
                      ? structuredContent.content
                      : undefined
                  setMessages((prev) =>
                    applyStreamComplete(prev, botMessage.id, data, {
                      text: fullContent,
                      structured,
                    })
                  )
                } else if (data.type === 'error') {
                  applyStreamError(data)
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === botMessage.id
                        ? {
                            ...msg,
                            content: data.error || t('askCL.errorRetry'),
                          }
                        : msg
                    )
                  )
                }
              } catch (e) {
                console.error('Error parsing streaming response:', e)
              }
            }
          }
        }

        setEditingTextareaValue('')
        setInputValue('')
        setUserMessageCount(userMessageCount + 1)
      } catch (error) {
        console.error('Error sending edited message:', error)
        const errorMessage: Message = {
          id: `error-${Date.now()}`,
          sender: 'bot',
          content: t('askCL.errorRetry'),
          timestamp: new Date(),
        }
        setMessages((prev) => [...prev, errorMessage])
      } finally {
        setProcessing(false)
      }
    })()
  }, [editingTextareaValue, messages, currentSessionId, userMessageCount, handleCancelEdit])

  // Note: File upload for forms is now handled through the form submission flow

  useEffect(() => {
    if (uploadedFormFile && uploadedFormFile.length > 0 && startUpload) {
      setValue(
        'businessPlanFile',
        uploadedFormFile.length === 1 ? uploadedFormFile[0] : uploadedFormFile
      )
      handleSubmit((formData) => submitDataConfirmationForm({ ...formData, skip: 'true' }))()
      setUploadedFormFile([])
      setStartUpload(false)
      resetField('businessPlanFile')
    }
  }, [
    startUpload,
    uploadedFormFile,
    setValue,
    handleSubmit,
    submitDataConfirmationForm,
    resetField,
  ])

  // Return null if feature flag is disabled (after all hooks are called)
  if (showFloatingChat === false) {
    return null
  }

  return (
    <div
      ref={floatingChatRef}
      className={`fixed z-50 ${isExpanded ? 'inset-0' : 'bottom-4 right-4'}`}
    >
      {/* Backdrop when expanded */}
      {isOpen && isExpanded && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={toggleExpand} />
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`bg-white rounded-lg flex flex-col transition-all duration-300 overflow-hidden ${
            isExpanded
              ? 'w-[100vw] h-[98vh] fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50'
              : 'w-[75vw] h-[75vh] mb-2'
          }`}
          style={{
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overscrollBehavior: 'contain',
          }}
          onWheel={(e) => {
            // Prevent scroll propagation to main page when hovering over chat
            // This prevents scroll chaining when reaching scroll boundaries
            e.stopPropagation()
            e.preventDefault()

            // Find the scrollable element that's being hovered
            let target = e.target as HTMLElement
            let scrollableElement: HTMLElement | null = null

            // Walk up the DOM tree to find the nearest scrollable element
            while (target && target !== e.currentTarget) {
              if (target.hasAttribute('data-scrollable')) {
                scrollableElement = target
                break
              }
              target = target.parentElement as HTMLElement
            }

            // If we found a scrollable element, manually scroll it
            if (scrollableElement) {
              scrollableElement.scrollTop += e.deltaY
            }
          }}
        >
          {/* Header */}
          <div className="rounded-t-lg bg-app-primary-125">
            <div className="flex items-center justify-between p-4 text-white">
              <div className="flex flex-col">
                <div className="font-semibold text-white text-base">
                  {t('askCL.messagesHeader')}
                </div>
                <div className="text-xs text-white opacity-90">{t('askCL.messagesSubheader')}</div>
                {showAdvisorChip && (
                  <div className="mt-1 inline-flex items-center gap-2 self-start max-w-full rounded-full bg-white bg-opacity-20 px-2 py-0.5 text-xs text-white">
                    {advisorChipName && (
                      <>
                        <span className="font-medium truncate">
                          {t('askCL.target.chip', { name: advisorChipName })}
                        </span>
                        <span
                          aria-hidden="true"
                          className="h-3 w-px shrink-0 bg-white opacity-50"
                        />
                      </>
                    )}
                    <span className="opacity-90 whitespace-nowrap">
                      {t('askCL.target.advisorOnly')}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {canShowMemory && (
                  <AppToolTip
                    name="floating-chat-memory-tooltip"
                    text={t('askCL.memory.open')}
                    variant="primary"
                    place="bottom"
                  >
                    <button
                      onClick={() => setShowMemory(true)}
                      aria-label={t('askCL.memory.open')}
                      aria-haspopup="dialog"
                      className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                    >
                      <NotebookText className="w-5 h-5" stroke="white" aria-hidden="true" />
                    </button>
                  </AppToolTip>
                )}
                <AppToolTip
                  name="floating-chat-help-tooltip"
                  text={t('askCL.tooltips.help')}
                  variant="primary"
                  place="bottom"
                >
                  <button
                    onClick={helpDesk}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                  >
                    <HelpCircle className="w-5 h-5" stroke="white" />
                  </button>
                </AppToolTip>
                <AppToolTip
                  name="floating-chat-folder-tooltip"
                  text={t('askCL.tooltips.viewSessions')}
                  variant="primary"
                  place="bottom"
                >
                  <button
                    onClick={handleFolderClick}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                  >
                    <FolderIcon className="w-5 h-5" />
                  </button>
                </AppToolTip>
                <AppToolTip
                  name="floating-chat-expand-tooltip"
                  text={t(isExpanded ? 'askCL.tooltips.minimize' : 'askCL.tooltips.expand')}
                  variant="primary"
                  place="bottom"
                >
                  <button
                    onClick={toggleExpand}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                  >
                    <ArrowsPointingInIcon className="w-5 h-5" />
                  </button>
                </AppToolTip>
                <AppToolTip
                  name="floating-chat-close-tooltip"
                  text={t('askCL.tooltips.close')}
                  variant="primary"
                  place="bottom"
                >
                  <button
                    onClick={toggleChat}
                    className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="white" viewBox="0 0 24 24">
                      <path
                        stroke="white"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </AppToolTip>
              </div>
            </div>
          </div>

          {/* Tabs - Grey background section */}
          <div className="px-4 py-3 border-b bg-app-grey-5 border-app-grey-25">
            <div className="flex bg-white rounded-lg p-1 shadow-sm">
              <button
                onClick={() => setActiveTab('coach')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'coach'
                    ? 'font-semibold bg-app-white-95 text-app-grey-100'
                    : 'hover:opacity-80 bg-transparent text-app-grey-65'
                }`}
              >
                <AppAvatar size="xs" type="image" shape="Rounded" src={Avatar} />
                {t('askCL.askTab')}
              </button>
              <button
                onClick={() => setActiveTab('chat')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'chat'
                    ? 'font-semibold bg-app-white-95 text-app-grey-100'
                    : 'hover:opacity-80 bg-transparent text-app-grey-65'
                }`}
              >
                {t('askCL.chatTab')}
              </button>
            </div>
          </div>

          {/* Session List */}
          {showSessionList && (
            <div className="border-b bg-white border-app-grey-25">
              <div className="p-4 max-h-80 overflow-y-auto" data-scrollable>
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-semibold text-gray-800">{t('askCL.chatHistory')}</h4>
                  <button
                    onClick={handleNewConversation}
                    className="px-3 py-1 text-sm rounded-md transition-colors bg-app-primary-125 text-white hover:bg-app-primary-150"
                  >
                    {t('askCL.newChat')}
                  </button>
                </div>

                {/* Search Input */}
                <div className="relative mb-4">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-4 w-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder={t('askCL.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-125 focus:border-transparent"
                  />
                </div>

                {/* Grouped Sessions */}
                <div className="space-y-4">
                  {Object.keys(groupedSessions).length > 0 ? (
                    Object.entries(groupedSessions).map(([dateGroup, sessions]) => (
                      <div key={dateGroup}>
                        <h5 className="text-sm font-medium text-gray-700 mb-2">{dateGroup}</h5>
                        <div className="space-y-1">
                          {sessions.map((session) => (
                            <button
                              key={session.id}
                              onClick={() => handleSessionSelect(session)}
                              className={`w-full text-left p-3 rounded-lg transition-colors hover:bg-gray-50 ${
                                currentSessionId === session.id
                                  ? 'bg-app-primary-25 border border-app-primary-125'
                                  : 'bg-transparent hover:bg-gray-50'
                              }`}
                            >
                              <div className="font-medium text-sm text-gray-800 truncate mb-1">
                                {session.title || t('askCL.generalChat')}
                              </div>
                              {session.lastMessage && (
                                <div className="text-xs text-gray-600 truncate">
                                  {toThreadPreview(session.lastMessage.content)}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 text-sm py-8">
                      {searchQuery ? t('askCL.noMatchingConversations') : t('askCL.noChatSessions')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden" data-scrollable>
            {activeTab === 'coach' ? (
              /* Ask Coach Logic Tab */
              <>
                {/* Loading state for fetching messages, or while the help
                    flow is bootstrapping a fresh session. */}
                {((isMessagesLoading && currentSessionId) ||
                  (isHelpInitializing && messages.length === 0)) && (
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                    <div className="mb-4">
                      <LoadingIndicator size={40} />
                    </div>
                    {isHelpInitializing ? (
                      <>
                        <p className="text-gray-700 text-sm font-medium">
                          {t('askCL.connectingHelp')}
                        </p>
                        <p className="text-gray-500 text-xs mt-2 max-w-xs">
                          {t('askCL.connectingHelpHint')}
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-600 text-sm">{t('askCL.loadingConversation')}</p>
                    )}
                  </div>
                )}
                {/* Welcome screen for new conversations */}
                {!isMessagesLoading && messages.length === 1 && !currentSessionId ? (
                  /* Initial welcome screen */
                  <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                    <div className="mb-6">
                      <AppAvatar size="xl" type="image" shape="Rounded" src={Avatar} />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">
                      {t('askCL.splashHeading')}
                    </h2>
                    <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                      {t('askCL.splashBody')}
                    </p>
                    <button
                      className="px-6 py-3 text-white rounded-lg font-medium transition-colors bg-app-primary-125"
                      onClick={() => {
                        if (!user?.id || isTyping) return

                        // Create a new GeneralChat session first
                        const target = coachTargetRef.current
                        createFlowChatSessionMutate(
                          {
                            input: generalChatSessionInput(target),
                          },
                          {
                            onSuccess: async (response: any) => {
                              // The advisor switched client meanwhile: this thread
                              // belongs to the previous one.
                              if (target !== coachTargetRef.current) return
                              const newSessionId = response.createFlowChatSession.id
                              console.log('Created new GeneralChat session:', newSessionId)

                              // Set the session ID
                              setCurrentSessionId(newSessionId)

                              // Send empty message to trigger bot greeting
                              await sendEmptyMessageToBot(newSessionId)
                            },
                            onError: (error: any) => {
                              console.error('Error creating chat session:', error)
                              const errorMessage: Message = {
                                id: `error-${Date.now()}`,
                                sender: 'bot',
                                content: t('askCL.createSessionError'),
                                timestamp: new Date(),
                              }
                              setMessages((prev) => [...prev, errorMessage])
                            },
                          }
                        )
                      }}
                      disabled={isTyping || !user?.id}
                    >
                      {t('askCL.startConversation')}
                    </button>
                  </div>
                ) : /* Chat messages - only show when not loading and have messages or session */
                !isMessagesLoading &&
                  !(isHelpInitializing && messages.length === 0) &&
                  (messages.length > 1 || currentSessionId) ? (
                  <div
                    className={`bg-app-grey-5 flex flex-col w-full p-2 sm:p-4 pt-0 overflow-y-scroll gap-4 ${
                      isExpanded ? 'h-[70vh]' : 'h-[100%]'
                    }`}
                    ref={chatContainerRef}
                    onScroll={handleUserScroll}
                    data-scrollable
                    style={{ overscrollBehavior: 'contain' }}
                  >
                    {isFetchingNextPage && (
                      <div className="flex justify-center p-1">
                        <LoadingIndicator size={20} />
                      </div>
                    )}
                    {messages
                      .filter((message) => {
                        // Filter out empty messages for both string and object content; an
                        // answer that is only a chart or diagram still shows
                        if (typeof message.content === 'string') {
                          return (
                            (message.content && message.content.trim() !== '') ||
                            (message.reportBlocks?.length ?? 0) > 0
                          )
                        }
                        return message.content // Objects are always considered valid
                      })
                      .map((message, index) => {
                        const messageKey = message.createdAt || message.id || `message-${index}`
                        const assistantVoiceText =
                          message.sender === 'bot' && typeof message.content === 'string'
                            ? extractTextForTTS(message.content)
                            : ''
                        const assistantContentVoiceText =
                          message.sender === 'bot' && typeof message.content === 'object'
                            ? extractTextForTTS(message.content)
                            : ''
                        const showVoicePlayback = !!assistantVoiceText
                        const showContentVoicePlayback = !!assistantContentVoiceText
                        const isVoiceActive = activeVoiceMessageKey === messageKey
                        if (message.isEditingMessage) {
                          return (
                            <React.Fragment key={message.id}>
                              <div className="flex gap-2 justify-start">
                                <div>
                                  <AppAvatar
                                    size={'md'}
                                    type="image"
                                    shape={'Rounded'}
                                    src={Avatar}
                                    color="Primary"
                                  />
                                </div>
                                <div className="flex-1 max-w-[90%]">
                                  {/* Header with pencil icon, "Editing message" text, and X icon */}
                                  <div className="flex justify-between items-center mb-3 p-3 bg-app-grey-20 rounded-lg">
                                    <div className="flex items-center gap-2">
                                      <PenIcon className="w-5 h-5" />
                                      <span className="text-sm font-medium text-app-grey-100">
                                        {t('askCL.editingMessage')}
                                      </span>
                                    </div>
                                    <button
                                      onClick={handleCancelEdit}
                                      className="p-1 hover:bg-app-grey-20 rounded transition-colors"
                                      aria-label={t('askCL.cancelEditing')}
                                    >
                                      <AppCrossIcon className="w-5 h-5" />
                                    </button>
                                  </div>

                                  {/* Render form or text based on step type */}
                                  {message.editingStepType === OnboardingFlowStepTypes.Question ? (
                                    // For text messages, show textarea input with Save button
                                    <div className="mb-4">
                                      {/* Textarea input */}
                                      <div className="flex flex-col rounded-lg px-4 py-3 border border-primary-appDarkBorder">
                                        <textarea
                                          rows={6}
                                          value={editingTextareaValue}
                                          onChange={(e) => setEditingTextareaValue(e.target.value)}
                                          placeholder={t('askCL.placeholder')}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                                              e.preventDefault()
                                              handleSaveEdit()
                                            } else if (e.key === 'Escape') {
                                              e.preventDefault()
                                              handleCancelEdit()
                                            }
                                          }}
                                          className="w-full resize-none font-inter text-base text-black-appDark leading-6 bg-transparent border-none focus-visible:ring-0 focus-visible:outline-none min-h-[120px]"
                                        />
                                        <div className="flex justify-end mt-2">
                                          <AppButton
                                            variant="primary"
                                            size="sm"
                                            label={t('askCL.saveEdit')}
                                            disabled={
                                              !editingTextareaValue?.trim() ||
                                              processing ||
                                              isTyping
                                            }
                                            onClick={handleSaveEdit}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ) : null}

                                  {/* Warning banner */}
                                  <div className="p-3 bg-app-primary-10 border border-app-primary-25 rounded-lg text-sm text-app-primary-100">
                                    <div className="flex items-start gap-2">
                                      <span className="text-app-primary-125 font-bold">!</span>
                                      <span>{t('askCL.editWarning')}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </React.Fragment>
                          )
                        }

                        return (
                          <React.Fragment key={message.id}>
                            <div
                              className={`flex gap-2 ${
                                message.sender === 'user' ? 'justify-end' : 'justify-start'
                              }`}
                            >
                              <div className={`${message.sender === 'user' ? 'order-2' : ''}`}>
                                <AppAvatar
                                  size={'md'}
                                  type={message.sender === 'user' && !logo ? 'text' : 'image'}
                                  shape={'Rounded'}
                                  src={message.sender === 'user' ? logo : Avatar}
                                  text={
                                    message.sender === 'user' && !logo
                                      ? getInitials({
                                          firstName: user?.firstName || '',
                                          lastName: user?.lastName || '',
                                        })
                                      : undefined
                                  }
                                  color="Primary"
                                />
                              </div>
                              {/* Render form content using AppDynamicChatMessage */}
                              <div>
                                {typeof message.content === 'object' && message.sender === 'bot' ? (
                                  <div className="flex-1">
                                    <AppDynamicChatMessage
                                      key={messageKey}
                                      content={message.content as UIComponentProps}
                                      onSubmit={submitDataConfirmationForm}
                                      typingAnimationCallback={handleTypingMessage}
                                      useTypingAnimation={index === messages.length - 1}
                                      isLastMessage={index === messages.length - 1}
                                      isSendingMessageTobot={processing}
                                      tools={tools}
                                      setIsUpload={setIsUpload}
                                      showUploadModal={showUploadModal}
                                      isUpload={isUpload}
                                      uploadedFile={uploadedFormFile}
                                      setUploadedFile={setUploadedFormFile}
                                      startUpload={startUpload}
                                      setStartUpload={setStartUpload}
                                      scrollToBottom={scrollToBottom}
                                      refetchBusinessGoal={() => {}} // Not needed for general chat
                                      refetchTool={refetchTools}
                                      addMessage={(msg: any) => {
                                        setMessages((prev) => [
                                          ...prev,
                                          {
                                            id: `user-${Date.now()}`,
                                            sender: 'user',
                                            content: msg.text || msg.content,
                                            timestamp: new Date(),
                                          },
                                        ])
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div className="flex justify-start">
                                    {message.sender === 'user' && message.isVoiceMessage ? (
                                      <AppVoiceIndicator
                                        transcript={
                                          typeof message.content === 'string'
                                            ? message.content
                                            : String(message.content)
                                        }
                                        duration={message.voiceDuration}
                                        className="max-w-[90%]"
                                      />
                                    ) : (
                                      <div
                                        className={`max-w-[90%] flex flex-col rounded-lg px-3 pt-2 pb-1.5 text-base !font-inter relative ${
                                          message.reportBlocks && message.reportBlocks.length > 0
                                            ? 'w-[90%]'
                                            : 'w-fit'
                                        } ${
                                          message.sender === 'bot'
                                            ? 'bg-app-grey-20 text-app-grey-100'
                                            : 'text-white bg-app-primary-125'
                                        }`}
                                      >
                                        {message.sender === 'bot' ? (
                                          <>
                                            {message.reasoningText &&
                                              message.reasoningText.trim().length > 0 && (
                                                <details className="mb-2 group">
                                                  <summary className="flex items-center gap-1.5 cursor-pointer text-xs text-app-grey-60 hover:text-app-grey-80 list-none select-none py-1 [&::-webkit-details-marker]:hidden">
                                                    <ChevronRight className="w-3.5 h-3.5 shrink-0 transition-transform group-open:rotate-90" />
                                                    <Brain className="w-3.5 h-3.5 shrink-0" />
                                                    <span className="font-medium">
                                                      {t('askCL.thoughts.label')}
                                                    </span>
                                                    <span className="text-app-grey-50">
                                                      ·{' '}
                                                      {t('askCL.thoughts.duration', {
                                                        seconds: Math.max(
                                                          1,
                                                          Math.round(
                                                            message.reasoningText.length / 30
                                                          )
                                                        ),
                                                      })}
                                                    </span>
                                                  </summary>
                                                  <div className="mt-1.5 ml-1 px-3 py-2 bg-app-grey-10 border-l-2 border-app-grey-30 rounded text-xs leading-relaxed text-app-grey-70 font-light max-h-60 overflow-y-auto overscroll-contain">
                                                    <SafeMarkdown className="chatbot-markdown">
                                                      {message.reasoningText}
                                                    </SafeMarkdown>
                                                  </div>
                                                </details>
                                              )}
                                            <SafeMarkdown
                                              className="chatbot-markdown"
                                              components={{ a: BotMessageLink }}
                                            >
                                              {typeof message.content === 'string'
                                                ? message.content
                                                : String(message.content)}
                                            </SafeMarkdown>
                                            {message.reportBlocks &&
                                              message.reportBlocks.length > 0 && (
                                                <CoachChatContext.Provider value={coachChat}>
                                                  <AppCoachReportBlocks
                                                    blocks={message.reportBlocks}
                                                  />
                                                </CoachChatContext.Provider>
                                              )}
                                          </>
                                        ) : (
                                          <SafeMarkdown
                                            className="whitespace-pre-wrap text-white [overflow-wrap:anywhere]"
                                            components={{
                                              a: UserMessageLink,
                                              p: WhiteParagraph,
                                              ul: WhiteUl,
                                              ol: WhiteOl,
                                              li: WhiteLi,
                                              strong: WhiteStrong,
                                              em: WhiteEm,
                                              h1: WhiteH1,
                                              h2: WhiteH2,
                                              h3: WhiteH3,
                                            }}
                                          >
                                            {typeof message.content === 'string'
                                              ? message.content
                                              : String(message.content)}
                                          </SafeMarkdown>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            {showVoicePlayback && (
                              <div className="flex justify-start ml-12">
                                <AppVoicePlayback
                                  audioUrl={isVoiceActive ? voiceAudioUrl : undefined}
                                  isLoading={isVoiceLoading && isVoiceActive}
                                  isSupported={isVoiceSupported}
                                  onToggle={() =>
                                    toggleVoicePlayback(messageKey, assistantVoiceText)
                                  }
                                  onPlaybackEnd={clearVoicePlayback}
                                />
                              </div>
                            )}
                            {showContentVoicePlayback && (
                              <div className="flex justify-start ml-12">
                                <AppVoicePlayback
                                  audioUrl={isVoiceActive ? voiceAudioUrl : undefined}
                                  isLoading={isVoiceLoading && isVoiceActive}
                                  isSupported={isVoiceSupported}
                                  onToggle={() =>
                                    toggleVoicePlayback(messageKey, assistantContentVoiceText)
                                  }
                                  onPlaybackEnd={clearVoicePlayback}
                                />
                              </div>
                            )}

                            {message.sender === 'bot' && index === 0 && userMessageCount === 0 && (
                              <div className="flex justify-start">
                                <div className="ml-12 max-w-[90%]">
                                  <div className="text-sm text-gray-700 font-medium mb-3">
                                    {t('askCL.quickSuggestions')}
                                  </div>
                                  <div className="space-y-2">
                                    {[
                                      t('askCL.suggestions.research'),
                                      t('askCL.suggestions.feedback'),
                                      t('askCL.suggestions.report'),
                                      t('askCL.suggestions.techTool'),
                                      t('askCL.suggestions.improvement'),
                                    ].map((suggestion, suggestionIndex) => (
                                      <button
                                        key={suggestionIndex}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        className="w-full text-left p-3 rounded-lg bg-app-white-95 hover:bg-app-grey-10 transition-colors text-sm text-app-grey-100 border border-app-grey-25 shadow-sm"
                                        disabled={isTyping || !user?.id}
                                      >
                                        {suggestion}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </React.Fragment>
                        )
                      })}

                    {(isTyping || isHelpInitializing) && (
                      <div className="flex items-center gap-2 self-start">
                        <div className="flex items-center space-x-1">
                          <div
                            className="rounded-full animate-bounce"
                            style={{ width: 8, height: 8, backgroundColor: '#4B5563' }}
                          />
                          <div
                            className="rounded-full animate-bounce animation-delay-150"
                            style={{ width: 8, height: 8, backgroundColor: '#4B5563' }}
                          />
                          <div
                            className="rounded-full animate-bounce animation-delay-300"
                            style={{ width: 8, height: 8, backgroundColor: '#4B5563' }}
                          />
                        </div>
                        <span className="text-gray-500 text-sm">{t('askCL.status.default')}</span>
                      </div>
                    )}

                    <div ref={messagesEndRef} />
                  </div>
                ) : null}
              </>
            ) : (
              /* Chat Tab - Stream Chat Integration */
              <div className="flex flex-col h-full">
                {user && token ? (
                  <Chat
                    user={user}
                    token={token}
                    chatUsers={chatUsers}
                    chatUnreadMessageCount={chatUnreadMessageCount}
                    usersAssessmentResponseByQuestion={usersAssessmentResponseByQuestion}
                    saveUserFileAction={saveUserFileAction}
                    getChatUsersAction={getChatUsersAction}
                    chatUnreadMessageCountAction={chatUnreadMessageCountAction}
                    getUsersAssessmentResponseByQuestionAction={
                      getUsersAssessmentResponseByQuestionAction
                    }
                  />
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                    <div className="mb-4">
                      <svg
                        className="w-16 h-16 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-6a2 2 0 012-2h8z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">
                      {t('askCL.teamChatLoading.title')}
                    </h3>
                    <p className="text-gray-500 text-sm">{t('askCL.teamChatLoading.body')}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Input - Only show when in Coach tab and conversation has started */}
          {activeTab === 'coach' && (messages.length > 1 || currentSessionId) && (
            <div className="p-4 border-gray-100">
              {(() => {
                const editingMessage = messages.find((msg) => msg.isEditingMessage)
                const isEditing = !!editingMessage

                return (
                  <div className="flex flex-col rounded-lg px-4 py-3 border border-primary-appDarkBorder">
                    {/* File preview UI */}
                    {pendingFiles.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {pendingFiles.map((pf, idx) => (
                          <div
                            key={pf.file.name + idx}
                            className="flex items-center gap-2 bg-app-grey-10 rounded p-2"
                          >
                            {pf.previewUrl ? (
                              <img
                                src={pf.previewUrl}
                                alt={pf.file.name}
                                className="w-10 h-10 object-cover rounded"
                              />
                            ) : (
                              <span className="w-10 h-10 flex items-center justify-center bg-app-grey-20 rounded text-xs">
                                {pf.file.name.split('.').pop()?.toUpperCase()}
                              </span>
                            )}
                            <span className="text-xs max-w-[100px] truncate">{pf.file.name}</span>
                            {pf.status === 'uploading' && (
                              <CircleNotchIcon className="animate-spin w-4 h-4 ml-1" />
                            )}
                            {pf.status === 'uploaded' && (
                              <span className="text-green-500 ml-1">✓</span>
                            )}
                            {pf.status !== 'uploading' && (
                              <button
                                className="ml-1 text-red-500 hover:text-red-700"
                                onClick={() => {
                                  setPendingFiles(pendingFiles.filter((_, i) => i !== idx))
                                }}
                                title={t('askCL.removeFile')}
                                type="button"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Textarea and send button */}
                    <textarea
                      disabled={
                        isTyping || processing || isFormStep || isToolConnectionStep || isEditing
                      }
                      rows={3}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder={t('askCL.placeholder')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                          e.preventDefault()
                          if (isEditing) {
                            handleSaveEdit()
                          } else {
                            handleSendMessage()
                          }
                        } else if (
                          (e.key === 'Enter' && e.shiftKey) ||
                          (e.key === 'Enter' && e.ctrlKey)
                        ) {
                          // Allow line break
                        } else if (e.key === 'Escape') {
                          e.preventDefault()
                          if (isEditing) {
                            handleCancelEdit()
                          } else {
                            setInputValue('')
                          }
                        } else if (e.key === 'Backspace' && e.ctrlKey) {
                          e.preventDefault()
                          setInputValue((prev) => {
                            // Delete the last word
                            const lastSpaceIndex = prev?.trimEnd().lastIndexOf(' ') ?? -1
                            return lastSpaceIndex !== -1
                              ? prev?.substring(0, lastSpaceIndex + 1) ?? ''
                              : ''
                          })
                        }
                      }}
                      className="w-full resize-none font-inter text-base text-black-appDark leading-5 bg-transparent border-none focus-visible:ring-0 focus-visible:outline-none"
                    />
                    <div className="flex justify-between relative w-full">
                      <div className="flex gap-2 items-center">
                        <AppButton
                          variant="clear"
                          size="sm"
                          RightIcon={AppPaperClipIcon}
                          disabled={isTyping}
                          onClick={() => setShowUploadModal(true)}
                        />
                        {/* Audio Recorder */}
                        {audioSupported && (
                          <AppAudioRecorder
                            onRecordingComplete={handleAudioRecording}
                            disabled={
                              isTyping ||
                              processing ||
                              isToolConnectionStep ||
                              pendingFiles.some((pf) => pf.status !== 'uploaded')
                            }
                            maxDuration={120} // 2 minutes
                            className="ml-1"
                          />
                        )}
                        <AppToolTip
                          name="create-play-tooltip"
                          text={t('chat.input.createPlay')}
                          variant="clear"
                        >
                          <AppButton
                            variant="clear"
                            size="sm"
                            RightIcon={AppPlayIcon}
                            disabled={isTyping}
                            onClick={() => setShowCreatePlayModal(true)}
                          />
                        </AppToolTip>
                      </div>

                      {isTyping ? (
                        <CircleNotchIcon className={'fill-primary h-[30px]'} />
                      ) : (
                        <AppButton
                          variant="primary"
                          size="sm"
                          RightIcon={AppSendMessageIcon}
                          disabled={
                            isTyping ||
                            processing ||
                            isFormStep ||
                            isToolConnectionStep ||
                            pendingFiles.some(
                              (pf) => pf.status === 'uploading' || pf.status === 'pending'
                            ) ||
                            (inputValue?.trim() === '' && pendingFiles.length === 0)
                          }
                          onClick={() => {
                            if (isEditing) {
                              handleSaveEdit()
                            } else {
                              handleSendMessage()
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                )
              })()}
            </div>
          )}
        </div>
      )}

      {/* Floating Chat Button */}
      {!isOpen && (
        // Smaller on phones, where the full-size button covers the text it floats over
        <button
          onClick={toggleChat}
          className="w-16 h-16 smd:w-[93px] smd:h-[93px] shadow-lg hover:scale-105 transition-all duration-200 flex items-center justify-center relative"
        >
          <QuickChatIcon className="w-16 h-16 smd:w-[93px] smd:h-[93px]" />
          {Number(chatUnreadMessageCount) > 0 && (
            <span className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 bg-primary-error text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold z-10 shadow-md">
              {chatUnreadMessageCount}
            </span>
          )}
        </button>
      )}
      {/* Upload Modal */}
      {isOpen && showMemory && canShowMemory && (
        <AppCoachMemory businessId={clientId} onClose={() => setShowMemory(false)} />
      )}

      {showUploadModal && (
        <AppModalBox title={t('askCL.uploadFiles')} onClose={() => setShowUploadModal(false)}>
          <div className="m-10">
            <AppFileUploadField
              name="chatFileUpload"
              className="flex-1"
              labelFontSize="base"
              maxFiles={5}
              onChange={(files) => {
                setPendingFiles(
                  files.map((file) => ({
                    file,
                    previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
                    status: 'pending',
                  }))
                )
              }}
            />
            <div className="mt-20 w-[30%]">
              <AppButton
                variant="primary"
                size="sm"
                label={t('files.upload')}
                disabled={
                  !pendingFiles.some((pf) => pf.status === 'pending') ||
                  uploadFileLoading ||
                  composerUploading
                }
                onClick={() => {
                  // Only files not uploaded yet: they wait in the composer until this upload
                  // gives them their links
                  const picked = pendingFiles.filter((pf) => pf.status === 'pending')
                  if (picked.length > 0 && user?.id) {
                    const uploadId = nextUploadId()
                    uploadRequestRef.current = uploadId
                    uploadFileAction({
                      files: picked.map((pf) => pf.file),
                      isMultiple: picked.length > 1,
                      saveFile: true,
                      requestId: uploadId,
                    })
                    setPendingFiles((prev) =>
                      prev.map((pf) =>
                        pf.status === 'pending' ? { ...pf, status: 'uploading', uploadId } : pf
                      )
                    )
                  }
                  setShowUploadModal(false)
                }}
              />
            </div>
            {(uploadFileLoading || composerUploading) && (
              <div className="mt-4 text-center text-sm text-gray-500">{t('files.uploading')}</div>
            )}
          </div>
        </AppModalBox>
      )}

      {/* Create Play Modal */}
      {showCreatePlayModal && (
        <AppModalBox
          Icon={AppCrossIcon}
          height="max-h-[95vh] min-h-[50vh] h-fit"
          width="w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[75vw] xl:max-w-2xl mx-2 sm:mx-4 md:mx-auto"
          titleSize="sm"
          title={t('chat.input.createNewPlay')}
          showBorder={false}
          onClose={() => setShowCreatePlayModal(false)}
          onBack={() => setShowCreatePlayModal(false)}
        >
          <AppTask
            tenantId={getTenantId()}
            userId={user?.id || ''}
            source={UserTaskSource.General}
            setShowTaskModal={setShowCreatePlayModal}
            setShowTaskSuccessModal={setShowPlaySuccessModal}
            setShowTaskErrorModal={setShowPlayErrorModal}
            setCreatedUserTask={handleSetCreatedUserTask}
            refetch={() => {}} // We'll handle refetch logic for play board sync later
            logo={logo || ''}
            userName={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || ''}
            advisorsData={advisorsData || []}
            userAvatar={user?.avatar || ''}
            userFirstName={user?.firstName || ''}
            userLastName={user?.lastName || ''}
            goalEndDate=""
            assigneeOptions={assigneeOptions}
            paramUserId={user?.id || ''}
            initialDescription="" // Empty description - no chat context
          />
        </AppModalBox>
      )}

      {/* Play Creation Success/Error Modal */}
      {(showPlaySuccessModal || showPlayErrorModal) && createdUserTask && (
        <AppTaskCreationConfirmation
          showAddTaskSuccessModal={showPlaySuccessModal}
          setShowAddTaskModal={setShowCreatePlayModal}
          setShowAddTaskSuccessModal={setShowPlaySuccessModal}
          setShowAddTaskErrorModal={setShowPlayErrorModal}
          userId={user?.id || ''}
          userAvatar={user?.avatar || ''}
          userFirstName={user?.firstName || ''}
          userLastName={user?.lastName || ''}
          assigneeOptions={assigneeOptions}
          createdUserTask={createdUserTask as UserTask}
        />
      )}
    </div>
  )
}

// Redux connection
const chatProps = (state: RootState) => {
  let { user, chatUsers } = state.user
  let { usersAssessmentResponseByQuestion } = state.owner
  return {
    user,
    chatUsers,
    token: user?.streamAccessToken,
    usersAssessmentResponseByQuestion,
    chatUnreadMessageCount: state.user.chatUnreadMessageCount,
    uploadFileLoading: state.common.uploadFileLoading,
    uploadedFile: state.common.uploadedFile,
    uploadFileRequestId: state.common.uploadFileRequestId,
    uploadedFileRequestId: state.common.uploadedFileRequestId,
  }
}

function chatDispatch(dispatch: Dispatch) {
  return bindActionCreators(
    {
      saveUserFileAction: saveUserFileAction.STARTED,
      getUsersAssessmentResponseByQuestionAction:
        getUsersAssessmentResponseByQuestionAction.STARTED,
      getChatUsersAction: getChatUsersAction.STARTED,
      chatUnreadMessageCountAction: chatUnreadMessageCountAction.STARTED,
      uploadFileAction: require('store/actions/common').uploadFileAction.STARTED,
    },
    dispatch
  )
}

export const AppFloatingChat = connect(chatProps, chatDispatch)(AppFloatingChatComponent)
